import { Button, cn, useTranslate } from '@athena/ui'

import { useFormat } from '../../lib/format.js'

import { startBlockedKey } from './startBlocked.js'

export interface AvailablePracticeCardProps {
  title: string
  subject: { name: string }
  canStart: boolean
  startBlockedReason: string | null
  availableUntil: string | null
  maxAttempts: number | null
  attemptsUsed: number
  /** The published version's time limit, so the card can say so before it is opened. */
  timed: boolean
  durationMinutes: number | null
  hasBeenAttempted: boolean
  onStart: () => void
  starting: boolean
  /** Small subject shelves use cards; larger shelves become one divided list. */
  presentation?: 'card' | 'row'
}

/**
 * One practice on the student's home.
 *
 * ── A card the student can start ─────────────────────────────────────────
 *
 * The whole card is the target, not only its button: the button's hit area is
 * stretched over the card with a pseudo-element, so a young student who taps
 * the title starts the practice too. It stays one real `<button>` — one tab
 * stop, one name, nothing nested — named for the practice ("Start Fractions"),
 * so a screen reader listing the buttons hears which is which rather than
 * "Start, Start, Start". The visible label is inside that name.
 *
 * The button is secondary. "Continue practice", above, is the one primary
 * action on the page; a column of indigo Starts competed with it and with
 * each other. An outlined button on a card that lifts on hover is still an
 * unmistakable thing to press.
 *
 * ── A card the student cannot start ──────────────────────────────────────
 *
 * Kept on the page, because why it cannot be started is worth knowing. It is
 * quieter by its surface — the recessed grey rather than white — and by
 * having no button, never by fading: the old 70% opacity took the reason's
 * own text below readable contrast. Every word on it is at full strength, and
 * the reason is said in a sentence, so nothing depends on the colour.
 */
export function AvailablePracticeCard({
  title,
  subject,
  canStart,
  startBlockedReason,
  availableUntil,
  maxAttempts,
  attemptsUsed,
  timed,
  durationMinutes,
  hasBeenAttempted,
  onStart,
  starting,
  presentation = 'card',
}: AvailablePracticeCardProps) {
  const t = useTranslate()
  const format = useFormat()

  // What it is about comes first: a student scanning their list is choosing
  // between practices, and "Mathematics" is what tells them apart.
  const detail = [
    format.categoryLine({ subject }),
    maxAttempts === null
      ? null
      : t('learn.card.attemptsLeft', [Math.max(maxAttempts - attemptsUsed, 0), maxAttempts]),
    availableUntil === null ? null : t('learn.card.until', [format.date(availableUntil)]),
    // On the card as well as in the dialog, so a student choosing what to do
    // next knows which of these needs an uninterrupted half hour.
    timed && durationMinutes !== null
      ? t('learn.card.timed', [format.duration(durationMinutes)])
      : null,
  ].filter((part): part is string => part !== null)

  return (
    <div
      className={cn(
        'relative w-full gap-3',
        presentation === 'card'
          ? 'flex max-w-2xl flex-col rounded-md border p-6'
          : 'grid rounded-sm px-3 py-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center',
        presentation === 'card'
          ? canStart
            ? 'border-border bg-surface transition-[box-shadow,border-color] hover:border-border-strong hover:shadow-elevation-1'
            : 'border-border bg-surface-secondary'
          : canStart
            ? 'transition-colors hover:bg-surface-secondary'
            : 'bg-surface-secondary',
      )}
      data-presentation={presentation}
    >
      <div className="flex flex-col gap-1">
        {/* The same size the tutor's practice card uses, so a practice looks
            like a practice on both sides of the product. */}
        <h3
          className={cn(
            'font-semibold text-text text-balance',
            presentation === 'card' ? 'text-h3' : 'text-h4',
          )}
        >
          {title}
        </h3>
        {detail.length > 0 ? (
          <p className="text-body-sm text-text-secondary">{detail.join(' · ')}</p>
        ) : null}
      </div>

      {canStart ? (
        <div>
          {/* Large, for the 44px touch target a student's controls keep. */}
          <Button
            variant="secondary"
            size="lg"
            onClick={onStart}
            loading={starting}
            loadingText={t('common.opening')}
            aria-label={t(hasBeenAttempted ? 'learn.card.tryAgainNamed' : 'learn.card.startNamed', [
              title,
            ])}
            // The stretched target: the whole card presses this button.
            className="after:absolute after:inset-0 after:rounded-md after:content-['']"
          >
            {hasBeenAttempted ? t('learn.card.tryAgain') : t('learn.card.start')}
          </Button>
        </div>
      ) : (
        // Said in words rather than shown as a dead button: a control that
        // does nothing invites a second click and explains nothing.
        <p className="text-body-sm font-semibold text-text">
          {t(startBlockedKey(startBlockedReason))}
        </p>
      )}
    </div>
  )
}
